// postMessage bridge protocol — mirrors PROTOCOL.md §2.
// Both sides ignore messages with unknown version or unknown type.

import type { Locale, SiteContext, ThemeName, UserHint } from './site';

export const PROTOCOL_VERSION = '1' as const;
export type ProtocolVersion = typeof PROTOCOL_VERSION;

export interface Envelope<TType extends string, TPayload> {
  version: ProtocolVersion;
  type: TType;
  payload: TPayload;
}

// ---------- Launcher → Panel ----------

export interface InitPayload {
  apiBase: string;
  siteContext?: SiteContext;
  user?: UserHint;
  /**
   * FEATURE-66 — locale on the init envelope is now optional and may
   * carry the wire-level `'auto'` sentinel:
   *   - `'vi'` / `'en'` — host pinned a language; panel forwards as-is.
   *   - `'auto'` — host wants BE-side auto-detection (FEATURE-42 /
   *     FEATURE-56); panel forwards `'auto'` on the chat POST.
   *   - omitted — host did not configure a language; panel omits
   *     `locale` from the chat POST entirely so the BE's auto-detect
   *     runs. Same observable BE behavior as `'auto'`; preserving the
   *     distinction keeps host intent visible in logs.
   * Additive within bridge v1 — older launcher builds always shipped
   * a concrete `'vi' | 'en'` and continue to validate.
   */
  locale?: Locale | 'auto';
  /**
   * Visual theme. Added in FEATURE-34 (additive within v1).
   * Absent → panel falls back to `'default'`.
   */
  theme?: ThemeName;
  /**
   * FEATURE-88 — multi-tenant client identifier. Propagated from the host's
   * `BAAssistant.init({ clientCode })` call (or the `<meta name="ba-assistant-client">`
   * fallback). Kebab-case, `^[a-z0-9-]{3,64}$`. Additive within bridge v1 —
   * older launcher builds omit the field and the panel reads it from the
   * iframe URL query string instead.
   */
  clientCode?: string;
  /**
   * Host brand/accent color (any CSS color). The panel converts it to an
   * RGB triplet and overrides its `--ba-color-brand*` variables so primary
   * surfaces (user bubbles, send button, focus rings, borders) match the
   * host brand. Additive within bridge v1 — absent → theme default. Also
   * mirrored on the iframe URL `?accent=` for pre-mount application.
   */
  accentColor?: string;
}

export type PanelMode = 'popup' | 'fullscreen';

export interface OpenPayload {
  mode: PanelMode;
}

export type ClosePayload = Record<string, never>;

export interface UpdateUserPayload {
  user: UserHint;
}

export interface UpdateContextPayload {
  siteContext: SiteContext;
}

/**
 * Runtime theme change from launcher to panel.
 * Added in FEATURE-34 (additive within v1).
 */
export interface UpdateThemePayload {
  theme: ThemeName;
}

export type LauncherToPanel =
  | Envelope<'init', InitPayload>
  | Envelope<'open', OpenPayload>
  | Envelope<'close', ClosePayload>
  | Envelope<'update-user', UpdateUserPayload>
  | Envelope<'update-context', UpdateContextPayload>
  | Envelope<'update-theme', UpdateThemePayload>;

// ---------- Panel → Launcher ----------

export type ReadyPayload = Record<string, never>;

export interface ResizePayload {
  height: number;
  width: number;
}

export type RequestFullscreenPayload = Record<string, never>;
export type ExitFullscreenPayload = Record<string, never>;
export type RequestClosePayload = Record<string, never>;

export interface AnalyticsEventPayload {
  name: string;
  props?: Record<string, unknown>;
}

export type PanelToLauncher =
  | Envelope<'ready', ReadyPayload>
  | Envelope<'resize', ResizePayload>
  | Envelope<'request-fullscreen', RequestFullscreenPayload>
  | Envelope<'exit-fullscreen', ExitFullscreenPayload>
  | Envelope<'request-close', RequestClosePayload>
  | Envelope<'analytics-event', AnalyticsEventPayload>;

// ---------- Type guards ----------

function isEnvelope(value: unknown): value is { version: unknown; type: unknown; payload: unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'version' in value &&
    'type' in value &&
    'payload' in value
  );
}

export function isLauncherToPanel(value: unknown): value is LauncherToPanel {
  if (!isEnvelope(value)) return false;
  if (value.version !== PROTOCOL_VERSION) return false;
  return (
    value.type === 'init' ||
    value.type === 'open' ||
    value.type === 'close' ||
    value.type === 'update-user' ||
    value.type === 'update-context' ||
    value.type === 'update-theme'
  );
}

export function isPanelToLauncher(value: unknown): value is PanelToLauncher {
  if (!isEnvelope(value)) return false;
  if (value.version !== PROTOCOL_VERSION) return false;
  return (
    value.type === 'ready' ||
    value.type === 'resize' ||
    value.type === 'request-fullscreen' ||
    value.type === 'exit-fullscreen' ||
    value.type === 'request-close' ||
    value.type === 'analytics-event'
  );
}

export function makeEnvelope<TType extends string, TPayload>(
  type: TType,
  payload: TPayload,
): Envelope<TType, TPayload> {
  return { version: PROTOCOL_VERSION, type, payload };
}
